import { useCallback, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useFlags, usePassQuest } from '../api/hooks';
import { ErrorView, Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { questAbout, questConditions, questSteps } from '../components/pass/questTexts';
import { shortDate } from '../format';

/** «Пт» — короткий день недели. */
function wd(iso: string): string {
  const w = new Date(iso).toLocaleDateString('ru-RU', { weekday: 'short' });
  return w.charAt(0).toUpperCase() + w.slice(1);
}
import { backButton, haptics } from '../telegram';

const DIFF_LABEL: Record<string, string> = { easy: 'Лёгкое', medium: 'Среднее', hard: 'Сложное' };

/** «до пн · 3д 14ч» из weekResetAt. */
function weekTimer(resetAt: string): string {
  const ms = Math.max(0, new Date(resetAt).getTime() - Date.now());
  const d = Math.floor(ms / 86400_000);
  const h = Math.floor((ms % 86400_000) / 3600_000);
  return `до пн · ${d}д ${h}ч`;
}

/** «на Пт 03.10» из даты игры. */
function gameChip(at: string): string {
  return `на ${wd(at)} ${shortDate(at)}`;
}

/**
 * 6.1d — экран задания (handoff 5 §4): карточка с прогрессом, «Что сделать»,
 * степпер «Как выполнить», условия, прогресс по играм, CTA к записи.
 * Доступен всем игрокам (решение владельца); награды в XP.
 */
export function QuestScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const { id } = useParams();
  const quest = usePassQuest(Number(id), enabled);
  const navigate = useNavigate();

  // Назад — к списку заданий: /pass с автооткрытой шторкой (replace, чтобы
  // системный «назад» с /pass не возвращал на этот экран).
  const toList = useCallback(
    () => navigate('/pass', { replace: true, state: { quests: true } }),
    [navigate],
  );
  useEffect(() => {
    backButton.show(toList);
    return () => backButton.hide(toList);
  }, [toList]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data || quest.isLoading) return <Loader />;
  if (quest.isError || !quest.data) return <ErrorView message={(quest.error as Error)?.message} />;
  const q = quest.data;
  const isGame = q.period === 'game';
  const pct = Math.min(100, Math.round((q.value / q.target) * 100));
  const registered = q.gameId != null || q.registeredGameAt != null;
  // Тексты из CRM главнее встроенных шаблонов по метрике
  const about = q.about?.trim() || questAbout(q.metric, q.target);
  const steps = q.steps?.length
    ? q.steps.map((s, i) => ({
        ...s,
        done: q.done || (i === 0 && registered),
      }))
    : questSteps(q.metric, q.target, { registered, done: q.done });
  const conditions = q.conditions?.length
    ? [
        ...q.conditions,
        ...(q.conditions.some((c) => c.text.toLowerCase().includes('заменить'))
          ? []
          : [{ ok: false, text: 'Заменить задание нельзя' }]),
      ]
    : questConditions(q.metric, q.target);
  const chipAt = isGame ? (q.gameStartAt ?? q.registeredGameAt) : null;

  return (
    <div className="bqd">
      {/* навбар: назад · «ЗАДАНИЕ · …» · чип-таймер */}
      <div className="bqd-nav">
        <button
          className="iconbtn"
          onClick={() => {
            haptics.tap();
            toList();
          }}
          aria-label="Назад"
        >
          <BackIcon />
        </button>
        <span className="t mono">ЗАДАНИЕ · {isGame ? 'НА ИГРУ' : 'НА НЕДЕЛЮ'}</span>
        <span className="chip mono">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7.5V12l3 2" />
          </svg>
          {isGame
            ? chipAt
              ? gameChip(chipAt)
              : 'запишись на игру'
            : q.weekResetAt
              ? weekTimer(q.weekResetAt)
              : ''}
        </span>
      </div>

      <div className="bqd-body">
        {/* карточка задания — как строка из шторки 6.1c */}
        <div className="bqd-card">
          <div className="row">
            <span className={`sq mono${q.done ? ' ok' : isGame ? ' game' : ''}`}>
              {q.done ? '✓' : `${q.value}/${q.target}`}
            </span>
            <span className="mid">
              <b>{q.title}</b>
              <i>
                {isGame
                  ? `На каждую игру · одно задание${q.difficulty ? ` · ${DIFF_LABEL[q.difficulty].toLowerCase()}` : ''}`
                  : 'Недельное · обновляется после каждой игры'}
              </i>
            </span>
            <span className="pill mono">+{q.reward} XP</span>
          </div>
          <div className="prog">
            <span className="bar">
              <i className={q.done ? 'ok' : ''} style={{ width: `${q.done ? 100 : pct}%` }} />
            </span>
            <span className="cnt mono">
              {q.value} из {q.target}
            </span>
          </div>
        </div>

        <div className="bqd-sec mono">ЧТО СДЕЛАТЬ</div>
        <p className="bqd-about">{about}</p>

        <div className="bqd-sec mono">КАК ВЫПОЛНИТЬ</div>
        <div className="bqd-steps">
          {steps.map((s, i) => (
            <div className="step" key={i}>
              <span className="track">
                <span className={`dot mono${s.done ? ' on' : ''}`}>{s.done ? '✓' : i + 1}</span>
                {i < steps.length - 1 && <i className="ln" />}
              </span>
              <span className="tx">
                <b>{s.title}</b>
                <i>{s.text}</i>
              </span>
            </div>
          ))}
        </div>

        <div className="bqd-conds">
          {conditions.map((c, i) => (
            <div className="cond" key={i}>
              {c.ok ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1f9d6b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M4 12.5 9.5 18 20 6.5" />
                </svg>
              ) : (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden className="no">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              )}
              <span>{c.text}</span>
            </div>
          ))}
        </div>

        {q.history.length > 0 && (
          <>
            <div className="bqd-sec mono">
              ПРОГРЕСС ПО ИГРАМ
              <span>{isGame ? 'эта игра' : 'эта неделя'}</span>
            </div>
            <div className="bqd-hist">
              {q.history.map((h, i) => (
                <div className="hrow" key={i}>
                  <span className="dt mono">
                    {wd(h.date)} {shortDate(h.date)}
                  </span>
                  <span className="tx">{h.delta > 0 ? 'Прогресс засчитан' : 'Без прогресса'}</span>
                  <span className={`d mono${h.delta > 0 ? ' on' : ''}`}>
                    {h.delta > 0 ? `+${h.delta}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* CTA прижат к низу поверх фейда */}
      <div className="bqd-cta">
        {q.done ? (
          <button
            className="btn-secondary bp-btn-secondary"
            onClick={() => {
              haptics.tap();
              toList();
            }}
          >
            К заданиям
          </button>
        ) : (
          <button
            className="btn"
            onClick={() => {
              haptics.tap();
              navigate('/');
            }}
          >
            {isGame ? 'К записи на игру' : 'Записаться на игру'}
          </button>
        )}
      </div>
    </div>
  );
}
